/**
 * The CSV side of Add guests > Import CSV (#1013, D12). The browser parses the file; the server
 * (`POST /events/{id}/attendees/import`) matches each row to a contact by email, creates the missing ones and
 * answers `{added, created, already_guests, skipped: [{row, email, reason}]}`.
 */
import { isValidEmail } from '../utils/emailValidation';

export const MAX_IMPORT_ROWS = 500;

/** Header spellings (lower-cased, punctuation removed) read as each field; every other column is ignored. */
const HEADER_ALIASES = {
    email: ['email', 'emailaddress', 'workemail', 'primaryemail'],
    first_name: ['firstname', 'first', 'givenname', 'forename'],
    last_name: ['lastname', 'last', 'surname', 'familyname'],
    company: ['company', 'companyname', 'organization', 'organisation', 'account', 'accountname'],
    title: ['title', 'jobtitle', 'position', 'role'],
    phone: ['phone', 'phonenumber', 'mobile', 'mobilephone', 'telephone', 'tel'],
};
const FIELD_OF = Object.fromEntries(Object.entries(HEADER_ALIASES).flatMap(([field, names]) => names.map((name) => [name, field])));
const headerKey = (cell) => String(cell || '').toLowerCase().replace(/[^a-z0-9]/g, '');

/** The delimiter the header line uses (comma, semicolon or tab; spreadsheets in some locales save semicolons). */
function detectDelimiter(text) {
    let first = '';
    let quoted = false;
    for (const ch of text) {
        if (ch === '"') quoted = !quoted;
        if (!quoted && (ch === '\n' || ch === '\r')) break;
        first += ch;
    }
    const count = (d) => first.split(d).length - 1;
    return [',', ';', '\t'].reduce((best, d) => (count(d) > count(best) ? d : best), ',');
}

/** RFC 4180 rows from `text`: quoted fields, doubled quotes, delimiters and newlines inside quotes, CRLF/CR/LF. */
export function parseCsvRows(input) {
    const text = String(input ?? '').replace(/^﻿/, '');
    const delimiter = detectDelimiter(text);
    const rows = [];
    let row = [];
    let cell = '';
    let quoted = false;
    const endCell = () => { row.push(cell); cell = ''; };
    const endRow = () => { endCell(); rows.push(row); row = []; };
    for (let i = 0; i < text.length; i += 1) {
        const ch = text[i];
        if (quoted) {
            if (ch === '"') {
                if (text[i + 1] === '"') { cell += '"'; i += 1; } else quoted = false;
            } else cell += ch;
        } else if (ch === '"' && cell === '') {
            quoted = true;
        } else if (ch === delimiter) {
            endCell();
        } else if (ch === '\n' || ch === '\r') {
            if (ch === '\r' && text[i + 1] === '\n') i += 1;
            endRow();
        } else cell += ch;
    }
    if (cell !== '' || row.length) endRow();
    return rows;
}

/**
 * Read an Add-guests CSV. The first row is the header and must hold an `email` column; first/last name,
 * company, title and phone are read when present, other columns ignored. Blank lines are dropped.
 *
 * Answers `{rows, fileRows, total, valid, error}`: `rows` are the request rows, `fileRows[i]` the 1-based data
 * row of `rows[i]` in the file (the server numbers the rows it is sent, so the result maps back through it),
 * `total` how many rows were found, `valid` how many have a plausible email, `error` the reason the file
 * cannot be imported (no rows are returned then).
 */
export function parseGuestsCsv(text, { maxRows = MAX_IMPORT_ROWS } = {}) {
    const fail = (error) => ({ rows: [], fileRows: [], total: 0, valid: 0, error });
    const all = parseCsvRows(text);
    const blank = (cells) => cells.every((c) => c.trim() === '');
    const headerAt = all.findIndex((cells) => !blank(cells));
    if (headerAt < 0) return fail('This file is empty.');
    const columns = {};
    all[headerAt].forEach((cell, index) => {
        const field = FIELD_OF[headerKey(cell)];
        if (field && columns[field] === undefined) columns[field] = index;
    });
    if (columns.email === undefined) return fail('The first row must be a header with an "email" column.');
    // Row numbers count the file's data rows, blank lines included, so they match what a spreadsheet shows.
    const kept = all.slice(headerAt + 1).map((cells, i) => ({ cells, fileRow: i + 1 })).filter(({ cells }) => !blank(cells));
    if (!kept.length) return fail('This file has a header but no guests below it.');
    if (kept.length > maxRows) return fail(`This file has ${kept.length} rows; the limit is ${maxRows} per import. Split it and import the parts.`);
    const rows = kept.map(({ cells }) => Object.fromEntries(Object.entries(columns)
        .map(([field, index]) => [field, String(cells[index] ?? '').trim()])
        .filter(([field, value]) => field === 'email' || value !== '')));
    return { rows, fileRows: kept.map(({ fileRow }) => fileRow), total: rows.length, valid: rows.filter((r) => isValidEmail(r.email)).length, error: null };
}

export const SKIP_REASONS = {
    invalid_email: 'Not a valid email address',
    duplicate_in_file: 'Appears earlier in the file',
    contact_not_visible: 'Belongs to a contact you cannot access',
};
export const skipReasonText = (reason) => SKIP_REASONS[reason] || String(reason || 'Skipped').replace(/_/g, ' ');
